import { Component, Inject, ChangeDetectionStrategy } from '@angular/core';

import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { EventDTO } from '../../../dto/event/event.dto';

export interface DunaCalendarDayEventsDialogData {
  date: Date;
  events: EventDTO[];
  showAddEvent?: boolean;
}

@Component({
  selector: 'app-duna-calendar-day-events-dialog',
  standalone: true,
  imports: [MatDialogModule, MatButtonModule, MatIconModule],
  template: `
    <div class="day-events-dialog">
      <h2 mat-dialog-title>
        <mat-icon>event</mat-icon>
        Begivenheder {{ formattedDate }}
      </h2>

      <mat-dialog-content>
        <p class="event-count">
          {{ data.events.length }} begivenhed{{
            data.events.length === 1 ? '' : 'er'
          }}
        </p>
        <ul class="event-list">
          @for (event of data.events; track event) {
          <li
            class="event-item"
            (click)="selectEvent(event)"
            (keydown.enter)="selectEvent(event)"
            tabindex="0"
            role="button"
          >
            <span
              class="event-type"
              [class]="'type-' + (event.event_type || 'other')"
            >
              {{ getEventTypeName(event.event_type) }}
            </span>
            <span class="event-title">{{ getEventTitle(event) }}</span>
            <span class="event-time">{{ formatEventTime(event) }}</span>
            <mat-icon class="chevron">chevron_right</mat-icon>
          </li>
          }
        </ul>
      </mat-dialog-content>

      <mat-dialog-actions align="end">
        @if (data.showAddEvent) {
        <button
          mat-raised-button
          color="primary"
          type="button"
          (click)="addNewEvent()"
        >
          <mat-icon>add</mat-icon>
          Tilføj begivenhed
        </button>
        }
        <button mat-button type="button" (click)="close()">Luk</button>
      </mat-dialog-actions>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.Eager,
  styles: [
    `
      .day-events-dialog h2[mat-dialog-title] {
        display: flex;
        align-items: center;
        gap: 8px;
        margin: 0;
        color: #244277;
        font-size: 18px;
      }

      .event-count {
        margin: 0 0 12px;
        color: #64748b;
        font-size: 14px;
      }

      .event-list {
        list-style: none;
        margin: 0;
        padding: 0;
        max-height: min(60vh, 420px);
        overflow-y: auto;
      }

      .event-item {
        display: grid;
        grid-template-columns: auto 1fr auto auto;
        gap: 8px 12px;
        align-items: center;
        padding: 12px;
        border: 1px solid #e2e8f0;
        border-radius: 8px;
        margin-bottom: 8px;
        cursor: pointer;
        transition: background-color 0.15s ease, border-color 0.15s ease;
      }

      .event-item:hover,
      .event-item:focus {
        background-color: #f8fafc;
        border-color: #244277;
        outline: none;
      }

      .event-type {
        font-size: 10px;
        font-weight: 600;
        text-transform: uppercase;
        padding: 3px 8px;
        border-radius: 12px;
        background: #e0e7ff;
        color: #3730a3;
        white-space: nowrap;
      }

      .event-title {
        font-weight: 500;
        color: #1e293b;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
      }

      .event-time {
        font-size: 12px;
        color: #64748b;
        white-space: nowrap;
      }

      .chevron {
        color: #94a3b8;
        font-size: 20px;
        width: 20px;
        height: 20px;
      }

      mat-dialog-actions button mat-icon {
        margin-right: 4px;
        vertical-align: middle;
        font-size: 18px;
        width: 18px;
        height: 18px;
      }
    `,
  ],
})
export class DunaCalendarDayEventsDialogComponent {
  formattedDate: string;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: DunaCalendarDayEventsDialogData,
    private dialogRef: MatDialogRef<DunaCalendarDayEventsDialogComponent>
  ) {
    this.formattedDate = data.date.toLocaleDateString('da-DK', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  }

  close(): void {
    this.dialogRef.close();
  }

  addNewEvent(): void {
    this.dialogRef.close({ action: 'add' });
  }

  selectEvent(event: EventDTO): void {
    if (event?.guid) {
      this.dialogRef.close({ guid: event.guid });
    }
  }

  getEventTitle(event: EventDTO): string {
    const e = event as any;
    if (e.title) {
      return e.title;
    }
    if (e.name) {
      return e.name;
    }
    const short = e.description?.short_description;
    if (short?.['da']?.translation_text) {
      return short['da'].translation_text;
    }
    if (short?.['en']?.translation_text) {
      return short['en'].translation_text;
    }
    if (short) {
      const lang = Object.keys(short)[0];
      if (lang && short[lang]?.translation_text) {
        return short[lang].translation_text;
      }
    }
    return 'Begivenhed';
  }

  getEventTypeName(eventType?: string): string {
    const names: Record<string, string> = {
      education: 'Uddannelse',
      course: 'Kursus',
      exercise: 'Øvelse',
      activity: 'Aktivitet',
      training: 'Træning',
      meeting: 'Møde',
      conference: 'Konference',
      workshop: 'Workshop',
      seminar: 'Seminar',
      other: 'Andet',
    };
    return names[eventType || ''] || 'Begivenhed';
  }

  formatEventTime(event: EventDTO): string {
    if (!event.start_date) {
      return '';
    }
    const start = new Date(event.start_date);
    if (isNaN(start.getTime())) {
      return '';
    }
    const timeOpts: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
    };
    const startTime = start.toLocaleTimeString('da-DK', timeOpts);
    if (!event.end_date) {
      return `kl. ${startTime}`;
    }
    const end = new Date(event.end_date);
    if (isNaN(end.getTime())) {
      return `kl. ${startTime}`;
    }
    const sameDay =
      start.getFullYear() === end.getFullYear() &&
      start.getMonth() === end.getMonth() &&
      start.getDate() === end.getDate();
    if (sameDay) {
      return `kl. ${startTime} – ${end.toLocaleTimeString('da-DK', timeOpts)}`;
    }
    return `${start.toLocaleDateString('da-DK')} – ${end.toLocaleDateString(
      'da-DK'
    )}`;
  }
}
